import { useCallback, useMemo, useRef, useState, type ComponentProps, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { DndContext, DragOverlay } from '@dnd-kit/core'

import zIndex from 'styles/tools/z-index'
import WhileDraggingStyle from 'tripPlanner/components/DragAndDrop/WhileDraggingStyle'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Switch from 'LuxKit/Switch/Switch'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import LineArrowUpIcon from 'LuxKit/Icons/line/LineArrowUpIcon'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import type TripListView from './TripListView'
import StopTimingEditControl from '../StopTimingEditControl'
import StopSearchActions from './StopSearchActions'
import TripListFiltersModal from './TripListFiltersModal'
import { useTripListFiltersModal } from './useTripListFiltersModal'
import {
  canMoveTripListRowToAnotherDay,
  getTripListBulkMoveDayOptions,
} from './tripListMoveDayOptions'
import { getTripListSelectedBookAction } from './tripListSelection'
import type { ItineraryScrollTarget } from '../../hooks/useItineraryUpdates'
import { useItineraryScrollTarget } from '../../hooks/useItineraryScrollTarget'
import TripListMobileRowCard from './TripListMobileRowCard'
import TripListQuickActions from './TripListQuickActions'
import { getTripListTypeLabel } from './tripListTypeLabels'
import {
  getTripListDateFilterOptions,
  getTripListLocationFilterOptions,
  getTripListRowCountByStatus,
  getTripListRowCountByType,
  getTripListStatusFilterOptions,
  getTripListTypeFilterOptions,
  hasTripListBookableOnlyFilterOption,
  hasTripListDateFilterOptions,
  hasTripListLocationFilterOptions,
  hasTripListStatusFilterOptions,
  hasTripListTypeFilterOptions,
} from './tripListFilterOptions'
import { useCloseDropdownWhenUnavailable } from './useCloseDropdownWhenUnavailable'
import { useTripListDragReorder } from './useTripListDragReorder'
import { DROP_ANIMATION_DURATION, autoScrollConfig, dropAnimationSideEffects } from './tripListReorder'
import type { TripListRowType, TripListStatusValue } from './tripListTypes'
import { isBookableTripListRow } from './tripListRows'
import { drawerBodyContentPaddingStyles } from '../drawerBodyLayout.styles'

type TripListViewProps = ComponentProps<typeof TripListView>
type TripListMobileGroup = TripListViewProps['groups'][number]

const TRIP_LIST_MOBILE_SCROLL_SELECTORS = {
  item: (target: ItineraryScrollTarget) => target.itemId !== undefined ? `[data-item-id="${target.itemId}"]` : undefined,
  day: (target: ItineraryScrollTarget) => target.dayId !== undefined ? `[data-day-id="${target.dayId}"]` : undefined,
  stop: (target: ItineraryScrollTarget) => `[data-stop-id="${target.stopId}"]`,
}

const MobileListPanel = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const ScrollArea = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: ${rem(24)};

  &.has-actions {
    padding-bottom: ${rem(112)};
  }
`

const Toolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  padding: ${rem(16)} 0 ${rem(12)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const FilterScroller = styled.div`
  display: flex;
  gap: ${rem(10)};
  align-items: center;
  min-width: 0;
  overflow-x: auto;
  padding: 0 ${rem(16)} ${rem(2)};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const SelectionToolbar = styled.div`
  padding: ${rem(16)} ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const CancelSelectionButton = styled.button`
  display: inline-flex;
  height: ${rem(38)};
  align-items: center;
  justify-content: center;
  padding: 0 ${rem(18)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;
`

const FilterChipButton = styled.button`
  display: inline-flex;
  height: ${rem(38)};
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: ${rem(7)};
  padding: 0 ${rem(14)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;

  &.is-active {
    border-color: ${props => props.theme.palette.brand.primary.normal};
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &:disabled,
  &:disabled:hover {
    cursor: default;
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

    svg {
      color: currentColor;
    }
  }
`

const FilterChipText = styled.span`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  font-weight: 400;
  white-space: nowrap;
`

const FilterChipDivider = styled.div`
  width: ${rem(1)};
  height: ${rem(32)};
  flex: 0 0 auto;
  background: ${props => props.theme.palette.neutral.surface.lightForeground};
`

const LuxPlusToggle = styled.label`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${rem(8)};
  height: ${rem(32)};
  padding: 0 ${rem(10)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  &:has(:disabled) {
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const GroupStack = styled.div`
  display: flex;
  flex-direction: column;
`

const dragLift = keyframes`
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(1.02);
    opacity: 0.94;
  }
`

const DragOverlayContent = styled.div`
  pointer-events: none;
  /* Promote to its own layer up front so the lift animates on the compositor and
     doesn't repaint the card underneath it. */
  will-change: transform, opacity;
  transform-origin: center left;
  animation: ${dragLift} 140ms cubic-bezier(0.2, 0, 0, 1) forwards;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1ms;
  }
`

const DayGroup = styled.section`
  display: flex;
  flex-direction: column;
`

const StopHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${rem(8)};
  padding: ${rem(12)} ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.darkBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  border-start-start-radius: ${props => props.theme.borderRadius.L};
  border-start-end-radius: ${props => props.theme.borderRadius.L};
`

const StopHeaderTitle = styled(BodyText)`
  min-width: 0;
`

const StopHeaderSubtitle = styled(Caption)`
  min-width: 0;
  max-width: 100%;
  color: inherit;
  opacity: 0.82;
`

interface MobileStopHeaderProps {
  group: TripListMobileGroup
  addDaysOrDatesLabel: string
  actionsDisabled: boolean
  isSelectionMode: boolean
  onSendStopTimingEditMessage: TripListViewProps['onSendStopTimingEditMessage']
}

function MobileStopHeader({
  group,
  addDaysOrDatesLabel,
  actionsDisabled,
  isSelectionMode,
  onSendStopTimingEditMessage,
}: MobileStopHeaderProps) {
  const stopHeaderRef = useRef<HTMLDivElement>(null)
  const stopHeader = group.stopHeader

  if (!stopHeader) return null

  return (
    <StopHeader
      ref={stopHeaderRef}
      data-stop-id={stopHeader.stop.id}
      data-trip-list-group-target-id={group.id}
      data-trip-list-group-target-placement="before"
    >
      <StopHeaderTitle variant="medium" weight="bold" lineClamp={1}>
        {stopHeader.title}
      </StopHeaderTitle>
      <StopHeaderSubtitle variant="large">
        <StopTimingEditControl
          stop={stopHeader.stop}
          onSendMessage={onSendStopTimingEditMessage}
          disabled={actionsDisabled || isSelectionMode}
          variant="mobile"
          dropdownAnchorRef={stopHeaderRef}
          expandTapTarget
        >
          {stopHeader.subtitle || addDaysOrDatesLabel}
        </StopTimingEditControl>
      </StopHeaderSubtitle>
    </StopHeader>
  )
}

const DayHeading = styled.div`
  padding: ${rem(12)} ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const EmptyStopPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(12)};
  padding: ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const EmptyStopBrowseActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const EmptyState = styled.div`
  display: flex;
  min-height: ${rem(220)};
  align-items: center;
  justify-content: center;
  ${drawerBodyContentPaddingStyles}
  text-align: center;
`

function getSortIcon(sort: TripListViewProps['sort']) {
  if (sort?.field !== 'price') return <LineAngleDownIcon size="XS" />
  return sort.direction === 'asc' ? <LineArrowUpIcon size="XS" /> : <LineArrowDownIcon size="XS" />
}

function TripListMobileView({
  groups,
  tripStops,
  allRows,
  visibleRows,
  selectedRows,
  selectedRowIds,
  filters,
  sort,
  luxPlusEnabled,
  onSelectRow,
  onDateFiltersChange,
  onLocationFiltersChange,
  onTypeFiltersChange,
  onStatusFiltersChange,
  onClearFilters,
  onSortToggle,
  onLuxPlusToggle,
  onRowClick,
  onBookRow,
  onBookAll,
  onViewBooking,
  onAskAi,
  onAskAiMove,
  onAskAiSelectedRows,
  onAskAiMoveSelectedRows,
  onDelete,
  onMoveToDay,
  onMoveSelectedRowsToDay,
  onDeleteSelectedRows,
  onClearSelection,
  onOpenStopHotels,
  onOpenStopFlights,
  onOpenStopExperiences,
  onSendStopTimingEditMessage,
  onUpdateNote,
  onUpdateStatus,
  bookingRowId,
  noteUpdateRowId,
  statusUpdateRowId,
  notesDisabled = false,
  statusDisabled = false,
  actionsDisabled = false,
  scrollTarget,
  onScrollComplete,
}: TripListViewProps) {
  const intl = useIntl()
  const [selectionMode, setSelectionMode] = useState(false)
  const {
    filtersModalOpen,
    focusedSection,
    openAllFilters,
    openDateFilters,
    openLocationFilters,
    openTypeFilters,
    openStatusFilters,
    closeFiltersModal,
    setFiltersModalOpen,
  } = useTripListFiltersModal()
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  useItineraryScrollTarget({
    scrollTarget,
    containerRef: scrollAreaRef,
    selectors: TRIP_LIST_MOBILE_SCROLL_SELECTORS,
    onScrollComplete,
  })

  const bookableRows = useMemo(() => visibleRows.filter(isBookableTripListRow), [visibleRows])
  const hasVisibleRows = visibleRows.length > 0
  const selectedBookAction = useMemo(
    () => getTripListSelectedBookAction(selectedRows),
    [selectedRows],
  )
  const showSelectedBookAction = selectedBookAction.kind !== 'none'
  const selectedRowsCanMoveToDay = selectedRows.length > 0 && selectedRows.every(canMoveTripListRowToAnotherDay)
  const selectedMoveDayOptions = useMemo(
    () => selectedRowsCanMoveToDay ? getTripListBulkMoveDayOptions(selectedRows, tripStops, intl) : [],
    [intl, selectedRows, selectedRowsCanMoveToDay, tripStops],
  )
  const showSelectedMoveWithAi = selectedRows.length > 0 && !selectedRowsCanMoveToDay
  const activeDateFilterCount = filters.dates.size
  const activeLocationFilterCount = filters.locations.size
  const activeTypeFilterCount = filters.types.size + (filters.bookableOnly ? 1 : 0)
  const activeStatusFilterCount = filters.statuses.size
  const activeFilterCount = activeDateFilterCount + activeLocationFilterCount + activeTypeFilterCount + activeStatusFilterCount
  const hasActiveSort = Boolean(sort)
  const hasActiveFilters = activeFilterCount > 0 || hasActiveSort
  const isSelectionMode = selectionMode || selectedRows.length > 0
  // A filtered or sorted list isn't showing the real itinerary order, so a drop
  // between two visible rows can't be turned into a meaningful move. Selection mode
  // reuses the card as a tap target for its checkbox, so drags are off there too.
  const dragActionsDisabled = actionsDisabled || hasActiveFilters || isSelectionMode
  const {
    orderedRenderableGroups,
    activeRenderableRow,
    overDragRowId,
    dropTargetPlacement,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  } = useTripListDragReorder({
    groups,
    visibleRows,
    selectedRowIds,
    bookingRowId,
    noteUpdateRowId,
    statusUpdateRowId,
    dragActionsDisabled,
    onMoveToDay,
  })

  const dateFilterOptions = useMemo(() => getTripListDateFilterOptions(allRows), [allRows])
  const hasDateFilterOptions = hasTripListDateFilterOptions(allRows)
  const locationFilterOptions = useMemo(() => getTripListLocationFilterOptions(allRows), [allRows])
  const hasLocationFilterOptions = hasTripListLocationFilterOptions(allRows)
  const rowCountByType = useMemo(() => getTripListRowCountByType(allRows), [allRows])
  const typeFilterOptions = useMemo(() => getTripListTypeFilterOptions(rowCountByType), [rowCountByType])
  const hasTypeFilterOptions = hasTripListTypeFilterOptions(allRows)
  const hasBookableOnlyFilterOption = hasTripListBookableOnlyFilterOption(allRows)
  const rowCountByStatus = useMemo(() => getTripListRowCountByStatus(allRows), [allRows])
  const statusFilterOptions = useMemo(() => getTripListStatusFilterOptions(rowCountByStatus), [rowCountByStatus])
  const hasStatusFilterOptions = hasTripListStatusFilterOptions(allRows)
  const hasAnyFilterOptions = (
    hasDateFilterOptions ||
    hasLocationFilterOptions ||
    hasTypeFilterOptions ||
    hasStatusFilterOptions
  )
  const typeLabelByType = useMemo<Record<TripListRowType, string>>(() => ({
    accommodation: getTripListTypeLabel(intl, 'accommodation'),
    experience: getTripListTypeLabel(intl, 'experience'),
    dining: getTripListTypeLabel(intl, 'dining'),
    flight: getTripListTypeLabel(intl, 'flight'),
    transport: getTripListTypeLabel(intl, 'transport'),
    cruise: getTripListTypeLabel(intl, 'cruise'),
    activity: getTripListTypeLabel(intl, 'activity'),
  }), [intl])

  const locationFilterLabel = intl.formatMessage({
    id: 'wL7VwjdiJ0_VYSYdHG-i',
    defaultMessage: 'Location',
    description: 'Filter chip label for locations in the mobile TripChat itinerary list view',
  })
  const dateFilterLabel = intl.formatMessage({
    id: 'aas_-qQnld7hAbKqjrO4',
    defaultMessage: 'Date',
    description: 'Date filter chip label in the trip planner list view',
  })
  const typeFilterLabel = intl.formatMessage({
    id: 'xo44I0qETkFUA6h4hsT_',
    defaultMessage: 'Type',
    description: 'Filter chip label for row types in the mobile TripChat itinerary list view',
  })
  const statusFilterLabel = intl.formatMessage({
    id: 'J2PAmmwxPPcYE8d-TnX1',
    defaultMessage: 'Status',
    description: 'Filter chip label for status values in the mobile TripChat itinerary list view',
  })
  const priceSortLabel = intl.formatMessage({
    id: '3fonh5ADDhpHAnfeOqxV',
    defaultMessage: 'Price',
    description: 'Sort chip label for prices in the mobile TripChat itinerary list view',
  })
  const resetAllFiltersLabel = intl.formatMessage({
    id: 'oKF-YpsO76LY7DtQ_8LB',
    defaultMessage: 'Reset all',
    description: 'Chip label that clears every filter and sort in the mobile TripChat itinerary list view',
  })
  const allFiltersLabel = intl.formatMessage({
    id: 'pCXqBUibT1T4ywjuUo2f',
    defaultMessage: 'All filters',
    description: 'Chip label that opens the modal holding every filter in the mobile TripChat itinerary list view',
  })
  const emptyStateLabel = intl.formatMessage({
    id: '_UyUJ_zU_m1kBjb_NaP1',
    defaultMessage: 'No itinerary items match these filters.',
    description: 'Empty state shown when mobile TripChat list filters remove all visible rows',
  })
  const selectVisibleLabel = intl.formatMessage({
    id: '2ml0Vs24Caua3wrTeIrT',
    defaultMessage: 'Select',
    description: 'Compact chip label for selecting all visible rows in the mobile TripChat itinerary list view',
  })
  const cancelSelectionLabel = intl.formatMessage({
    id: 'aig_YCtPqbzdOU6KEhVS',
    defaultMessage: 'Cancel',
    description: 'Button label for exiting selection mode in the mobile TripChat itinerary list view',
  })
  const sortByPriceLabel = intl.formatMessage({
    id: 'ncrSZD71Ff065xEJElWi',
    defaultMessage: 'Sort by price',
    description: 'Accessible label for sorting mobile TripChat itinerary list rows by price',
  })
  const addDaysOrDatesLabel = intl.formatMessage({
    id: 'tCy9PycI1Guoepw6mk8q',
    defaultMessage: 'Add days or dates',
    description: 'Action text shown on a trip planner stop with no dates or day count',
  })

  const handleDateFilterToggle = useCallback((date: string) => {
    const next = new Set(filters.dates)
    if (next.has(date)) {
      next.delete(date)
    } else {
      next.add(date)
    }
    onDateFiltersChange(next)
  }, [filters.dates, onDateFiltersChange])

  const handleLocationFilterToggle = useCallback((location: string) => {
    const next = new Set(filters.locations)
    if (next.has(location)) {
      next.delete(location)
    } else {
      next.add(location)
    }
    onLocationFiltersChange(next)
  }, [filters.locations, onLocationFiltersChange])

  const handleTypeFilterToggle = useCallback((type: TripListRowType) => {
    const next = new Set(filters.types)
    if (next.has(type)) {
      next.delete(type)
    } else {
      next.add(type)
    }
    onTypeFiltersChange(next, filters.bookableOnly)
  }, [filters.bookableOnly, filters.types, onTypeFiltersChange])

  const handleBookableOnlyToggle = useCallback(() => {
    onTypeFiltersChange(new Set(filters.types), !filters.bookableOnly)
  }, [filters.bookableOnly, filters.types, onTypeFiltersChange])

  const handleStatusFilterToggle = useCallback((status: TripListStatusValue) => {
    const next = new Set(filters.statuses)
    if (next.has(status)) {
      next.delete(status)
    } else {
      next.add(status)
    }
    onStatusFiltersChange(next)
  }, [filters.statuses, onStatusFiltersChange])

  const handleClearDateFilters = useCallback(() => {
    onDateFiltersChange(new Set())
  }, [onDateFiltersChange])

  const handleClearLocationFilters = useCallback(() => {
    onLocationFiltersChange(new Set())
  }, [onLocationFiltersChange])

  const handleClearTypeFilters = useCallback(() => {
    onTypeFiltersChange(new Set(), false)
  }, [onTypeFiltersChange])

  const handleClearStatusFilters = useCallback(() => {
    onStatusFiltersChange(new Set())
  }, [onStatusFiltersChange])

  const handlePriceSortClick = useCallback(() => {
    onSortToggle('price')
  }, [onSortToggle])

  const handleSelectionModeOpen = useCallback(() => {
    closeFiltersModal()
    setSelectionMode(true)
  }, [closeFiltersModal])

  const handleSelectionModeCancel = useCallback(() => {
    setSelectionMode(false)
    onClearSelection()
  }, [onClearSelection])

  const isSelectedBookingPending = Boolean(bookingRowId)

  const handleSelectedBook = useCallback(() => {
    if (isSelectedBookingPending) return
    if (selectedBookAction.kind === 'row') {
      onBookRow(selectedBookAction.row)
      return
    }
    if (selectedBookAction.kind === 'cart') {
      onBookAll()
    }
  }, [isSelectedBookingPending, onBookAll, onBookRow, selectedBookAction])

  const stopControlEvent = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  useCloseDropdownWhenUnavailable(hasAnyFilterOptions, setFiltersModalOpen)

  return (
    <MobileListPanel data-testid="trip-list-mobile-view">
      <ScrollArea ref={scrollAreaRef} className={clsx(selectedRows.length > 0 && 'has-actions')}>
        {!isSelectionMode && (
          <Toolbar>
            <FilterScroller onClick={stopControlEvent}>
              <FilterChipButton
                type="button"
                onClick={handleSelectionModeOpen}
                disabled={actionsDisabled || !hasVisibleRows}
              >
                <FilterChipText>{selectVisibleLabel}</FilterChipText>
              </FilterChipButton>
              {hasAnyFilterOptions && <FilterChipDivider aria-hidden="true" />}
              {hasAnyFilterOptions && (
                <FilterChipButton
                  type="button"
                  onClick={openAllFilters}
                  className={activeFilterCount > 0 ? 'is-active' : undefined}
                >
                  <LineFilterAltIcon size="XS" />
                  <FilterChipText>{allFiltersLabel}</FilterChipText>
                  {activeFilterCount > 0 && <Caption variant="medium" weight="bold">{activeFilterCount}</Caption>}
                </FilterChipButton>
              )}
              {hasDateFilterOptions && (
                <FilterChipButton
                  type="button"
                  onClick={openDateFilters}
                  className={activeDateFilterCount > 0 ? 'is-active' : undefined}
                >
                  <FilterChipText>{dateFilterLabel}</FilterChipText>
                  {activeDateFilterCount > 0 && <Caption variant="medium" weight="bold">{activeDateFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
              )}
              {hasLocationFilterOptions && (
                <FilterChipButton
                  type="button"
                  onClick={openLocationFilters}
                  className={activeLocationFilterCount > 0 ? 'is-active' : undefined}
                >
                  <FilterChipText>{locationFilterLabel}</FilterChipText>
                  {activeLocationFilterCount > 0 && <Caption variant="medium" weight="bold">{activeLocationFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
              )}
              {hasTypeFilterOptions && (
                <FilterChipButton
                  type="button"
                  onClick={openTypeFilters}
                  className={activeTypeFilterCount > 0 ? 'is-active' : undefined}
                >
                  <FilterChipText>{typeFilterLabel}</FilterChipText>
                  {activeTypeFilterCount > 0 && <Caption variant="medium" weight="bold">{activeTypeFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
              )}
              {hasStatusFilterOptions && (
                <FilterChipButton
                  type="button"
                  onClick={openStatusFilters}
                  className={activeStatusFilterCount > 0 ? 'is-active' : undefined}
                >
                  <FilterChipText>{statusFilterLabel}</FilterChipText>
                  {activeStatusFilterCount > 0 && <Caption variant="medium" weight="bold">{activeStatusFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
              )}
              <FilterChipButton
                type="button"
                onClick={handlePriceSortClick}
                className={sort?.field === 'price' ? 'is-active' : undefined}
                aria-label={sortByPriceLabel}
                disabled={!hasVisibleRows}
              >
                <FilterChipText>{priceSortLabel}</FilterChipText>
                {getSortIcon(sort)}
              </FilterChipButton>
              {hasActiveFilters && (
                <FilterChipButton type="button" onClick={onClearFilters}>
                  <LineTimesIcon size="XS" />
                  <FilterChipText>{resetAllFiltersLabel}</FilterChipText>
                </FilterChipButton>
              )}
              <LuxPlusToggle>
                <LuxPlusLogoWrapper height={10} />
                <ProductPaletteProvider palette="lux-plus">
                  <Switch checked={luxPlusEnabled} onChange={onLuxPlusToggle} disabled={!hasVisibleRows} />
                </ProductPaletteProvider>
              </LuxPlusToggle>
            </FilterScroller>
          </Toolbar>
        )}
        {isSelectionMode && (
          <SelectionToolbar>
            <CancelSelectionButton type="button" onClick={handleSelectionModeCancel}>
              <FilterChipText>{cancelSelectionLabel}</FilterChipText>
            </CancelSelectionButton>
          </SelectionToolbar>
        )}
        {groups.length === 0 && (
          <EmptyState>
            <BodyText variant="medium" colour="light-foreground-alt">
              {emptyStateLabel}
            </BodyText>
          </EmptyState>
        )}
        {groups.length > 0 && (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            autoScroll={autoScrollConfig}
          >
            <GroupStack>
              {orderedRenderableGroups.map(group => (
                <DayGroup key={group.id}>
                  <MobileStopHeader
                    group={group.group}
                    addDaysOrDatesLabel={addDaysOrDatesLabel}
                    actionsDisabled={actionsDisabled}
                    isSelectionMode={isSelectionMode}
                    onSendStopTimingEditMessage={onSendStopTimingEditMessage}
                  />
                  <DayHeading
                    data-trip-list-group-target-id={group.id}
                    data-trip-list-group-target-placement="before"
                  >
                    <BodyText variant="medium" weight="bold">{group.group.title}</BodyText>
                    {group.group.subtitle && (
                      <Caption variant="large" colour="light-foreground-alt">
                        {group.group.subtitle}
                      </Caption>
                    )}
                  </DayHeading>
                  {group.rows.map((rowView, rowIndex, rowsInGroup) => {
                    const placement = rowView.row.id === overDragRowId ? dropTargetPlacement : undefined
                    // Only show the insertion line between two cards, not at a group's
                    // top/bottom edge (before the first row or after the last row).
                    const isGroupEdge =
                      (placement === 'before' && rowIndex === 0) ||
                      (placement === 'after' && rowIndex === rowsInGroup.length - 1)
                    return (
                      <TripListMobileRowCard
                        key={rowView.row.id}
                        row={rowView.row}
                        tripStops={tripStops}
                        selected={rowView.selected}
                        selectionMode={isSelectionMode}
                        luxPlusEnabled={luxPlusEnabled}
                        isBookingPending={rowView.isBookingPending}
                        isNoteUpdatePending={rowView.isNoteUpdatePending}
                        isStatusUpdatePending={rowView.isStatusUpdatePending}
                        notesDisabled={notesDisabled}
                        statusDisabled={statusDisabled}
                        actionsDisabled={actionsDisabled}
                        dragDisabled={dragActionsDisabled}
                        dropTargetPlacement={isGroupEdge ? undefined : placement}
                        onSelectRow={onSelectRow}
                        onRowClick={onRowClick}
                        onBookRow={onBookRow}
                        onViewBooking={onViewBooking}
                        onAskAi={onAskAi}
                        onAskAiMove={onAskAiMove}
                        onDelete={onDelete}
                        onMoveToDay={onMoveToDay}
                        onUpdateNote={onUpdateNote}
                        onUpdateStatus={onUpdateStatus}
                      />
                    )
                  })}
                  {group.group.showEmptyPlaceholder && (
                    <EmptyStopPlaceholder
                      data-trip-list-group-target-id={group.id}
                      data-trip-list-group-target-placement="after"
                    >
                      <BodyText variant="medium" colour="light-foreground-alt">
                        {intl.formatMessage({
                          id: 'dukav_e-eCPWiO_9pp-2',
                          defaultMessage: "You don't have anything planned in {placeName} yet",
                          description: 'Placeholder text for an empty stop in the trip planner list view',
                        }, {
                          placeName: group.group.stopHeader?.title ?? group.group.title,
                        })}
                      </BodyText>
                      <EmptyStopBrowseActions>
                        {group.group.stopHeader && group.group.stopSearchActions && (
                          <StopSearchActions
                            actions={group.group.stopSearchActions}
                            stop={group.group.stopHeader.stop}
                            variant="default"
                            onOpenStopHotels={onOpenStopHotels}
                            onOpenStopFlights={onOpenStopFlights}
                            onOpenStopExperiences={onOpenStopExperiences}
                          />
                        )}
                      </EmptyStopBrowseActions>
                    </EmptyStopPlaceholder>
                  )}
                </DayGroup>
              ))}
            </GroupStack>
            <DragOverlay
              dropAnimation={{
                duration: DROP_ANIMATION_DURATION,
                easing: 'ease-out',
                sideEffects: dropAnimationSideEffects,
              }}
              zIndex={zIndex.draggingItem}
            >
              {activeRenderableRow && (
                <DragOverlayContent>
                  <TripListMobileRowCard
                    row={activeRenderableRow.row}
                    tripStops={tripStops}
                    selected={activeRenderableRow.selected}
                    selectionMode={false}
                    luxPlusEnabled={luxPlusEnabled}
                    isBookingPending={activeRenderableRow.isBookingPending}
                    isNoteUpdatePending={activeRenderableRow.isNoteUpdatePending}
                    isStatusUpdatePending={activeRenderableRow.isStatusUpdatePending}
                    notesDisabled
                    statusDisabled
                    actionsDisabled
                    dragDisabled
                    isDragOverlay
                    onSelectRow={onSelectRow}
                    onRowClick={onRowClick}
                    onBookRow={onBookRow}
                    onViewBooking={onViewBooking}
                    onAskAi={onAskAi}
                    onAskAiMove={onAskAiMove}
                    onDelete={onDelete}
                    onMoveToDay={onMoveToDay}
                    onUpdateNote={onUpdateNote}
                    onUpdateStatus={onUpdateStatus}
                  />
                  <WhileDraggingStyle $grabbingCursor={false} />
                </DragOverlayContent>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </ScrollArea>
      {selectedRows.length > 0 && (
        <TripListQuickActions
          variant="mobile"
          selectedCount={selectedRows.length}
          disabled={actionsDisabled}
          showBookAction={showSelectedBookAction}
          isBookingPending={isSelectedBookingPending}
          moveDayOptions={selectedMoveDayOptions}
          showMoveWithAi={showSelectedMoveWithAi}
          onBook={handleSelectedBook}
          onAskAi={onAskAiSelectedRows}
          onMoveToDay={onMoveSelectedRowsToDay}
          onMoveWithAi={onAskAiMoveSelectedRows}
          onDelete={onDeleteSelectedRows}
        />
      )}
      <TripListFiltersModal
        open={filtersModalOpen}
        focusedSection={focusedSection}
        filters={filters}
        dateFilterOptions={dateFilterOptions}
        locationFilterOptions={locationFilterOptions}
        typeFilterOptions={typeFilterOptions}
        statusFilterOptions={statusFilterOptions}
        rowCountByType={rowCountByType}
        rowCountByStatus={rowCountByStatus}
        typeLabelByType={typeLabelByType}
        bookableRowCount={bookableRows.length}
        showBookableOnlyOption={hasBookableOnlyFilterOption}
        hasActiveFilters={hasActiveFilters}
        onClose={closeFiltersModal}
        onDateFilterToggle={handleDateFilterToggle}
        onLocationFilterToggle={handleLocationFilterToggle}
        onTypeFilterToggle={handleTypeFilterToggle}
        onBookableOnlyToggle={handleBookableOnlyToggle}
        onStatusFilterToggle={handleStatusFilterToggle}
        onClearDateFilters={handleClearDateFilters}
        onClearLocationFilters={handleClearLocationFilters}
        onClearTypeFilters={handleClearTypeFilters}
        onClearStatusFilters={handleClearStatusFilters}
        onClearAllFilters={onClearFilters}
      />
    </MobileListPanel>
  )
}

export default TripListMobileView
